import type { ExportItem, ExportResult } from './types';

/** Which of an album's assets this browser has already exported. */
export interface ExportHistory {
  assetIds: string[];
  lastExportedAt: string | null;
}

export type HistoryStore = Pick<Storage, 'getItem' | 'setItem'>;

const NO_HISTORY: ExportHistory = { assetIds: [], lastExportedAt: null };
const keyFor = (albumId: string) => `kizu.albumExport.v1.${albumId}`;

function browserStore(): HistoryStore | null {
  try {
    return window.localStorage;
  } catch {
    return null;   // no window, or storage blocked
  }
}

/** Never throws: blocked, missing or corrupted storage reads as "nothing exported yet". */
export function loadExportHistory(albumId: string, store: HistoryStore | null = browserStore()): ExportHistory {
  if (!store || !albumId) return NO_HISTORY;
  try {
    const parsed = JSON.parse(store.getItem(keyFor(albumId)) ?? 'null');
    if (!parsed || !Array.isArray(parsed.assetIds)) return NO_HISTORY;
    return {
      assetIds: parsed.assetIds.filter((id: unknown): id is string => typeof id === 'string'),
      lastExportedAt: typeof parsed.lastExportedAt === 'string' ? parsed.lastExportedAt : null,
    };
  } catch {
    return NO_HISTORY;
  }
}

/** Adds ids to an album's history. Never throws; an empty list changes nothing. */
export function recordExported(
  albumId: string,
  ids: string[],
  now: Date = new Date(),
  store: HistoryStore | null = browserStore(),
): ExportHistory {
  const previous = loadExportHistory(albumId, store);
  if (ids.length === 0 || !albumId) return previous;
  const next: ExportHistory = {
    assetIds: [...new Set([...previous.assetIds, ...ids])],
    lastExportedAt: now.toISOString(),
  };
  try {
    store?.setItem(keyFor(albumId), JSON.stringify(next));
  } catch {
    // Storage full or blocked: the export itself still succeeded.
  }
  return next;
}

export function splitByHistory(items: ExportItem[], history: ExportHistory): { fresh: ExportItem[]; previous: ExportItem[] } {
  const exported = new Set(history.assetIds);
  return {
    fresh: items.filter(item => !exported.has(item.id)),
    previous: items.filter(item => exported.has(item.id)),
  };
}

/** The asset ids a finished run should add to the history. */
export function idsToRecord(mode: 'folder' | 'zip', result: ExportResult): string[] {
  // An interrupted ZIP run discards its open part, so nothing from it is safely on disk.
  if (mode === 'zip' && result.outcome !== 'complete') return [];
  return [...result.saved, ...result.skipped].map(item => item.id);
}
